/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai).
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export interface ExecutionProgress {
  node_id: string;
  node_title?: string;
  stage: string;
  agent_id?: string;
  agent_name?: string;
  tool_name?: string;
  elapsed_ms?: number;
  execution_id?: string;
  invocation_id?: string;
  parent_invocation_id?: string;
  call_id?: string;
  reasoning_content?: string;
  tool_arguments?: string;
  tool_result?: string;
  intent_result?: string;
}

const MIN_FENCE_LENGTH = 3;
const MILLISECONDS_PER_SECOND = 1000;

// Render tool payloads as literal code, even when they contain Markdown fences.
const literal = (value: string) => {
  const fences = value.match(/`+/g) || [];
  const fence = '`'.repeat(
    Math.max(MIN_FENCE_LENGTH, ...fences.map(item => item.length + 1)),
  );
  return `${fence}\n${value}\n${fence}\n`;
};

export const formatExecutionProgress = (
  progress: ExecutionProgress,
  localizedStatus?: string,
): string => {
  const name = progress.agent_name || progress.node_title || 'Agent';
  const tool = progress.tool_name || 'tool';
  const status: Record<string, string> = {
    calling_agent: `Calling ${name}`,
    thinking: `${name} is thinking`,
    tool_running: `${name} is using ${tool}`,
    tool_completed: `${tool} completed`,
    answering: `${name} is generating an answer`,
    completed: `${name} completed`,
    failed: `${name} failed`,
    intent_running: `${progress.node_title || 'Intent recognition'}: recognizing intent`,
    intent_completed: `${progress.node_title || 'Intent recognition'}: intent recognized`,
  };
  // Reasoning is delta text. Do not repeat a heading between every token.
  if (progress.stage === 'thinking' && progress.reasoning_content) {
    return progress.reasoning_content;
  }
  const label = localizedStatus || status[progress.stage];
  if (!label) {
    return '';
  }
  const elapsed = progress.elapsed_ms
    ? ` · ${(progress.elapsed_ms / MILLISECONDS_PER_SECOND).toFixed(1)}s`
    : '';
  let text = `\n${label}${elapsed}\n`;
  if (progress.call_id) {
    text += literal(progress.call_id);
  }
  for (const payload of [
    progress.tool_arguments,
    progress.tool_result,
    progress.intent_result,
  ]) {
    if (payload) {
      text += literal(payload);
    }
  }
  if (progress.reasoning_content) {
    text += progress.reasoning_content;
  }
  return text;
};

export const isExecutionProgressFinished = (progress: ExecutionProgress) =>
  ['completed', 'failed', 'intent_completed'].includes(progress.stage) ||
  (!progress.invocation_id && progress.stage === 'answering');
